export const PE_ROUTE = '/program-evaluation';

export type ListInfo = { title: string; verb: string; from: string; fromTo: string };

export type ListContext = {
  cohortLabels?: Record<string, string>;
  gradeLabels?: Record<string | number, string>;
};

const HEADLINE: Record<string, [string, string]> = {
  active: ['Active EL students', 'in the program now'],
  exited: ['Exited this year', 'left the program this year'],
  monitored: ['Monitored EL students', 'in monitoring'],
  met: ['Met exit criteria', 'met the exit criteria'],
  review: ['Eligible for review', 'met the review bar, not the exit bar'],
  newcomer: ['Newcomer ELs', 'are newcomers'],
  ltel: ['Long-term ELs', 'are long-term ELs'],
  dual: ['Dually identified', 'are dually identified'],
  newid: ['Newly identified', 'were identified this year'],
  served: ['Served this year', 'were served this year'],
  atrisk: ['At risk of becoming long-term', 'are a year or two from long-term'],
};

const NOTES: Record<string, [string, string]> = {
  unreadable_status: ["EL status we couldn't read", "have an EL status we couldn't read"],
  no_signal: ['Roster rows with no EL signal', 'carry no EL signal'],
  exit_undated: ['Exits with no date', 'exited with no date on file'],
  newcomer_confirm: ['Newcomer standing needs confirmation', 'need their newcomer standing confirmed'],
  ltel_confirm: ['Long-term standing needs confirmation', 'need their long-term standing confirmed'],
  exit_conflict: ['Exit dates that conflict with later testing', 'have an exit date that conflicts with later testing'],
  monitoring_disagrees: [
    'Monitoring status disagrees with the exit date',
    'have a monitoring status that disagrees with the exit date',
  ],
};

const START_LABELS: Record<string, string> = {
  1: 'started at level 1',
  2: 'started at level 2',
  3: 'started at level 3',
  4: 'started at level 4 or above',
  none: 'had no score last year',
};

const COHORT_LABELS: Record<string, string> = {
  ltel: 'Long-term ELs',
  newcomer: 'Newcomers',
  review: 'Eligible for review',
  dual: 'Dually identified',
};

const encode = (segment: string): string => encodeURIComponent(segment);

export const schoolKeyComponent = (school: string): string => school.replace(/%/g, '%25').replace(/:/g, '%3A');
const decodeSchool = (component: string): string => component.replace(/%3A/gi, ':').replace(/%25/g, '%') || 'No school on file';

export const gradeHref = (grade: number | string): string => `${PE_ROUTE}/grade/${encode(String(grade))}`;
export const BLANK_SCHOOL_PARAM = ':';
const schoolParamOf = (component: string): string =>
  component === '' || component === '.' || component === '..' ? `${BLANK_SCHOOL_PARAM}${component}` : component;
export const schoolHref = (component: string): string => `${PE_ROUTE}/school/${encode(schoolParamOf(component))}`;
export function schoolComponentOf(key: string): string {
  const parts = key.split(':');
  const at = parts.indexOf('school');
  return at === -1 ? '' : (parts[at + 1] ?? '');
}
export const schoolParamToComponent = (param: string): string => (param.startsWith(BLANK_SCHOOL_PARAM) ? param.slice(BLANK_SCHOOL_PARAM.length) : param);
export const cohortHref = (cohort: string): string => `${PE_ROUTE}/cohort/${encode(cohort)}`;
export const studentsHref = (key: string): string => `${PE_ROUTE}/students/${encode(key)}`;
export const studentHref = (ref: string, key: string): string =>
  `${PE_ROUTE}/student/${encode(ref)}/${encode(key)}`;

export const GRADE_NOT_ON_FILE = 'Not on file';
export const GRADE_PRE_K = -3;
export const GRADE_UNKNOWN = -1;

export function gradeShortLabel(grade: number, label?: string): string {
  if (grade === GRADE_PRE_K) return 'PK';
  if (grade < 0) return 'None';
  return grade === 0 ? 'K' : label || String(grade);
}

export function gradeName(grade: number | string, label?: string): string {
  const numeric = typeof grade === 'number' ? grade : Number.parseInt(grade, 10);
  if (numeric === GRADE_PRE_K || /^(pk|pre-k|prek)$/i.test(label ?? '')) return 'Pre-K';
  if (numeric < 0 || label === GRADE_NOT_ON_FILE) return 'Grade not on file';
  if (numeric === 0 || /^(k|kg|kindergarten)$/i.test(label ?? '')) return 'Kindergarten';
  const shown = Number.isNaN(numeric) ? (label ?? String(grade)) : String(numeric);
  return `Grade ${shown}`;
}

export function describeListKey(key: string, ctx: ListContext = {}): ListInfo {
  const parts = key.split(':');
  const [family, second, third, fourth] = parts;
  const grade = (g: string) => gradeName(g, ctx.gradeLabels?.[g]);
  const lower = (g: string) => grade(g).toLowerCase();
  if (family === 'note' && second && NOTES[second]) {
    const [title, verb] = NOTES[second] as [string, string];
    return { title, verb, from: 'About these counts', fromTo: PE_ROUTE };
  }
  if (family === 'grade' && second !== undefined) {
    if (third === 'school' && fourth !== undefined) {
      const school = decodeSchool(fourth);
      const met = parts[4] === 'met';
      return {
        title: `${grade(second)} · ${school}${met ? ' · met exit criteria' : ''}`,
        verb: `in ${lower(second)} at ${school}${met ? ' met the exit criteria' : ''}`,
        from: `Grade detail · ${grade(second)}`,
        fromTo: gradeHref(second),
      };
    }
    if (third === 'others') {
      return {
        title: `${grade(second)} · all other ELs`,
        verb: `in ${lower(second)} are neither newcomers nor long-term ELs`,
        from: `Grade detail · ${grade(second)}`,
        fromTo: gradeHref(second),
      };
    }
    if (third === 'met') {
      return {
        title: `${grade(second)} · met exit criteria`,
        verb: `in ${lower(second)} met the exit criteria`,
        from: 'Section 2.2 · Composite growth by grade',
        fromTo: PE_ROUTE,
      };
    }
    if (third === 'start' && fourth !== undefined) {
      const start = START_LABELS[fourth] ?? `started at ${fourth}`;
      return {
        title: `${grade(second)} · ${start}`,
        verb: `in ${lower(second)} ${start}`,
        from: `Grade detail · ${grade(second)}`,
        fromTo: gradeHref(second),
      };
    }
    return {
      title: grade(second),
      verb: `are in ${lower(second)}`,
      from: 'Section 2.2 · Composite growth by grade',
      fromTo: PE_ROUTE,
    };
  }
  if (family === 'school' && second !== undefined) {
    const school = decodeSchool(second);
    if (third === 'met') {
      return {
        title: `${school} · met exit criteria`,
        verb: `at ${school} met the exit criteria`,
        from: `School detail · ${school}`,
        fromTo: schoolHref(second),
      };
    }
    const gradePart = third === 'grade' && fourth !== undefined ? grade(fourth) : null;
    const start = parts[4] === 'start' && parts[5] !== undefined ? (START_LABELS[parts[5]] ?? `started at ${parts[5]}`) : null;
    return {
      title: `${school}${gradePart ? ` · ${gradePart}` : ''}${start ? ` · ${start}` : ''}`,
      verb: `at ${school}${gradePart ? `, ${gradePart.toLowerCase()}` : ''}${start ? `, ${start}` : ''}`,
      from: `School detail · ${school}`,
      fromTo: schoolHref(second),
    };
  }
  if (family === 'cohort' && second !== undefined) {
    const label = ctx.cohortLabels?.[second] ?? COHORT_LABELS[second] ?? second;
    const base = HEADLINE[second]?.[1] ?? 'are in this cohort';
    if (third === 'grade' && fourth !== undefined) {
      const met = parts[4] === 'met';
      return {
        title: `${label} · ${grade(fourth)}${met ? ' · met exit criteria' : ''}`,
        verb: `${base}, ${lower(fourth)}${met ? ', who met the exit criteria' : ''}`,
        from: `Cohort detail · ${label}`,
        fromTo: cohortHref(second),
      };
    }
    if (third === 'school' && fourth !== undefined) {
      const school = decodeSchool(fourth);
      const met = parts[4] === 'met';
      return {
        title: `${label} · ${school}${met ? ' · met exit criteria' : ''}`,
        verb: `${base}, ${school}${met ? ', who met the exit criteria' : ''}`,
        from: `Cohort detail · ${label}`,
        fromTo: cohortHref(second),
      };
    }
    if (third === 'met') {
      return { title: `${label} · met exit criteria`, verb: `${base} who met the exit criteria`, from: `Cohort detail · ${label}`, fromTo: cohortHref(second) };
    }
    return { title: label, verb: base, from: 'Section 4 · Cohort baselines', fromTo: PE_ROUTE };
  }
  const head = HEADLINE[key];
  if (head) {
    const title = COHORT_LABELS[key] && ctx.cohortLabels?.[key] ? ctx.cohortLabels[key] : head[0];
    return { title: title ?? head[0], verb: head[1], from: 'Headline numbers', fromTo: PE_ROUTE };
  }
  return { title: key, verb: 'are in this count', from: 'Headline numbers', fromTo: PE_ROUTE };
}
